/**
 * Per-tab cache of the account config the edge worker reports back.
 *
 * Why this exists: the tracking level is baked into the `<script>` snippet, so a
 * customer who changes it in the UI has to re-paste the snippet for the change to
 * reach the browser. The worker reports the stored level on every accepted batch
 * (`X-Emblue-Config`); we cache it here and apply it at the *next* `init` in this
 * tab, so an installed pixel converges on its own.
 *
 * This is cooperative, best-effort and deliberately not authoritative — the
 * worker's own `filterEventsByLevel` remains the enforcement. All this does is
 * stop useless data leaving the visitor's browser.
 *
 * `sessionStorage`, not `localStorage`: the SDK has never used `localStorage`
 * (see the note in `./queue.ts`), and per-tab scope bounds the failure mode of a
 * `active: false` entry to one tab. The stored value carries no visitor
 * identifier — it is the account's setting — so it is not consent-relevant, the
 * same reasoning `auto-tracking/pageview.ts` already relies on.
 */
import type { TrackingLevel } from './types';
import { LEVEL_PRESETS, normalizeLevel } from './config';
import { log } from './log';
import { readSession, writeSession } from './storage';
import { nowMs } from './utils';

/** Same `emb.*` convention as `emb.lastpv` in `auto-tracking/pageview.ts`. */
const STORAGE_KEY = 'emb.cfg';

/**
 * Duplicated from the worker's `src/ingest/config-header.ts` — the two are
 * separate builds (worker = tsc, SDK = esbuild IIFE) with no shared module, so
 * there is nothing to import. Change one, change the other.
 */
export const CONFIG_HEADER_NAME = 'X-Emblue-Config';

/**
 * How long an `active: false` entry is trusted. This TTL is load-bearing: a muted
 * queue sends nothing, so nothing refreshes the cache, so without an expiry the
 * tab would latch silent for its whole life. 15 minutes, mirroring the reasoning
 * behind the worker's own `NEGATIVE_TTL_SECONDS` in `src/security/config-cache.ts`.
 *
 * Positive entries get no TTL on purpose — every batch response rewrites them and
 * the entry dies with the tab, so a TTL would be a knob that never fires.
 */
const NEGATIVE_TTL_MS = 15 * 60 * 1000;

export interface RemoteConfig {
	level: TrackingLevel;
	/** Inferred from a 403, never sent on the wire — `resolveWriteKey` returns
	 *  null for both an inactive and an unknown key, so the edge cannot tell them
	 *  apart either. Any 403 means "stop sending". */
	active: boolean;
}

interface StoredConfig extends RemoteConfig {
	/** The write key the entry was learned for, so a page whose key changed
	 *  invalidates the entry instead of reading a stale one. */
	wk: string;
	ts: number;
}

/** Reads the cached config for `writeKey`, or null when there is nothing usable.
 *  Never throws — the SDK runs on someone else's page. */
export function readCachedConfig(writeKey: string | null): RemoteConfig | null {
	if (!writeKey) return null;

	const raw = readSession(STORAGE_KEY);
	if (!raw) return null;

	let parsed: StoredConfig;
	try {
		parsed = JSON.parse(raw) as StoredConfig;
	} catch {
		return null;
	}

	if (!parsed || typeof parsed !== 'object') return null;
	if (parsed.wk !== writeKey) return null;
	// Unrecognized level → null rather than the default, so a corrupt entry falls
	// back to whatever the snippet said instead of silently forcing `standard`.
	if (!LEVEL_PRESETS[parsed.level]) return null;
	if (typeof parsed.active !== 'boolean') return null;

	if (!parsed.active) {
		const ts = typeof parsed.ts === 'number' ? parsed.ts : 0;
		if (nowMs() - ts >= NEGATIVE_TTL_MS) return null;
	}

	return { level: parsed.level, active: parsed.active };
}

/** Persists the config learned from an ingest response. Never throws. */
export function storeConfig(writeKey: string | null, value: RemoteConfig): void {
	if (!writeKey) return;
	const entry: StoredConfig = { wk: writeKey, level: value.level, active: value.active, ts: nowMs() };
	try {
		writeSession(STORAGE_KEY, JSON.stringify(entry));
	} catch {
		// writeSession already swallows; belt and braces for JSON.stringify.
	}
}

/** Hashed identity the edge resolved from a click handoff. Validated in
 *  `Identity.absorbHandback` — parsed loosely here so a shape we don't recognize
 *  is dropped there rather than voiding the level we came for. */
export interface RemoteIdentity {
	email_sha256?: unknown;
	contact_ref?: unknown;
}

/** Parses the worker's `X-Emblue-Config` header. Returns null for anything
 *  unusable rather than defaulting, so a garbage header cannot overwrite a good
 *  cached value. */
export function parseConfigHeader(raw: string | null): { level: TrackingLevel; identity?: RemoteIdentity } | null {
	if (!raw) return null;
	let parsed: unknown;
	try {
		parsed = JSON.parse(raw);
	} catch {
		log('remote-config: unparseable header', raw);
		return null;
	}
	if (!parsed || typeof parsed !== 'object') return null;
	const level = (parsed as { level?: unknown }).level;
	// normalizeLevel would coerce an unknown level to `standard`; here we want to
	// know it was unknown, so validate against the presets first.
	if (!LEVEL_PRESETS[level as TrackingLevel]) return null;

	const identity = (parsed as { identity?: unknown }).identity;
	if (identity && typeof identity === 'object') {
		return { level: normalizeLevel(level), identity: identity as RemoteIdentity };
	}
	return { level: normalizeLevel(level) };
}
